import { use, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  CheckCircle2, Clock, AlertCircle, TrendingUp,
  User, CalendarDays, Pencil,
} from 'lucide-react';
import { getProgressColor } from '../../../helpers/Project-Report/utils/helpers';
import { listStatusConfig } from '../../../helpers/Project-Report/utils/statusConfig';

// ─── Stat chip (internal) ──────────────────────────────────────────────────────
const StatChip = ({ label, value, color, icon: Icon }) => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '6px 12px',
      borderRadius: '8px',
      background: color,
      minWidth: '64px',
      gap: '2px',
    }}
  >
    <div style={{ fontSize: '14px', fontWeight: '600', color: '#fff', letterSpacing: '0.5px', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '4px' }}>
      <Icon size={14} color="#fff" />
      {label}
    </div>
    <div style={{ fontSize: '22px', fontWeight: '800', color: '#fff', lineHeight: 1 }}>
      {value}
    </div>
  </div>
);

// ─── Project Card ──────────────────────────────────────────────────────────────
const ProjectCard = ({ project, categoryColor, categoryGradient, index, category, categoryId, onEdit, user }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [hovered, setHovered] = useState(false);
  const { bar: barColor } = getProgressColor(project.progress);
  const status = listStatusConfig[project.status] || listStatusConfig.Berlangsung;
  const StatusIcon = status.icon;
  const handleClick = () => {
    const path = `/project-report/${categoryId}/${project.id}`;
  
    navigate({
      pathname: path,
      search: searchParams.toString(),
    }, { 
      state: { project, category }
    });
  };

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={handleClick}
      style={{
        background: '#fff',
        borderRadius: '16px',
        border: hovered ? `1.5px solid ${categoryColor}40` : '1.5px solid #f1f5f9',
        boxShadow: hovered ? `0 12px 32px rgba(0,0,0,0.10)` : '0 2px 8px rgba(0,0,0,0.05)',
        transition: 'all 0.25s cubic-bezier(0.4,0,0.2,1)',
        transform: hovered ? 'translateY(-3px)' : 'none',
        overflow: 'hidden',
        animation: `fadeSlideIn 0.4s ease forwards`,
        animationDelay: `${index * 0.07}s`,
        opacity: 0,
        cursor: 'pointer',
      }}
    >

      {/* Top accent */}
      <div style={{ height: '3px', background: hovered ? categoryGradient : '#f1f5f9', transition: 'background 0.3s' }} />

      <div style={{ padding: '20px 24px' }}>
        {/* Row 1: index + title + status badge */}
        <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start', marginBottom: '10px' }}>
          {/* Index bubble */}
          <div
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              background: hovered ? categoryGradient : '#f8fafc',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '12px',
              fontWeight: '700',
              color: hovered ? '#fff' : '#94a3b8',
              flexShrink: 0,
              transition: 'all 0.25s',
            }}
          >
            {index + 1}
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
              <h3
                style={{
                  fontSize: '21px',
                  fontWeight: '700',
                  color: hovered ? categoryColor : '#0f172a',
                  margin: 0,
                  lineHeight: '1.4',
                  transition: 'color 0.2s',
                  letterSpacing: '-0.2px',
                }}
              >
                {project.title}
              </h3>
              {/* Status badge */}
              <span
                style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  padding: '4px 12px',
                  borderRadius: '20px',
                  background: status.bg,
                  color: status.color,
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                }}
              >
                <StatusIcon size={11} />
                {status.label}
              </span>
            </div>

            {/* Description */}
            <p
              style={{
                fontSize: '16px',
                color: '#64748b',
                margin: '6px 0 0',
                lineHeight: '1.6',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {project.description}
            </p>
          </div>
        </div>

        {/* Meta row: PIC + date */}
        <div
          style={{
            display: 'flex',
            gap: '16px',
            marginBottom: '16px',
            paddingLeft: '46px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '16px', color: '#94a3b8' }}>
            <User size={16} />
            <span>{project.picName}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '16px', color: '#94a3b8' }}>
            <CalendarDays size={16} />
            <span>{project.startDate} → {project.endDate}</span>
          </div>
        </div>

        {/* Progress bar + % */}
        <div style={{ paddingLeft: '46px', marginBottom: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
            <span style={{ fontSize: '14px', color: '#94a3b8', fontWeight: '500' }}>Progress Keseluruhan</span>
            <div
              style={{
                fontSize: '13px',
                fontWeight: '700',
                color: '#fff',
                background: barColor,
                padding: '2px 10px',
                borderRadius: '20px',
                minWidth: '46px',
                textAlign: 'center',
              }}
            >
              {project.progress}%
            </div>
          </div>
          <div style={{ height: '7px', background: '#f1f5f9', borderRadius: '99px', overflow: 'hidden' }}>
            <div
              style={{
                height: '100%',
                width: `${project.progress}%`,
                background: barColor,
                borderRadius: '99px',
                transition: 'width 0.8s cubic-bezier(0.4,0,0.2,1)',
              }}
            />
          </div>
        </div>

        {/* Task stat chips */}
        <div
          style={{
            paddingLeft: '46px',
            display: 'flex',
            gap: '8px',
            flexWrap: 'wrap',
            borderTop: '1px solid #f8fafc',
            paddingTop: '14px',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <StatChip label="On Progress" value={project.tasks.onProgress} color="#6366f1" icon={TrendingUp} />
            <StatChip label="Waiting"     value={project.tasks.waiting}    color="#f59e0b" icon={Clock}      />
            <StatChip label="On Hold"     value={project.tasks.onHold}     color="#ef4444" icon={AlertCircle}/>
            <StatChip label="Done"        value={project.tasks.done}       color="#10b981" icon={CheckCircle2}/>
          </div>
          {(onEdit && String(project?.UserNikCreated) === String(user?.nik)) && (
            <button
              onClick={(e) => { e.stopPropagation(); onEdit(project); }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
                padding: '7px 14px',
                borderRadius: '8px',
                border: `1.5px solid ${categoryColor}40`,
                background: hovered ? `${categoryColor}12` : '#f8fafc',
                color: categoryColor,
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s',
                flexShrink: 0,
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = `${categoryColor}20`; e.currentTarget.style.borderColor = categoryColor; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = hovered ? `${categoryColor}12` : '#f8fafc'; e.currentTarget.style.borderColor = `${categoryColor}40`; }}
            >
              <Pencil size={12} />
              Edit
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectCard;
